import { AxiosRequestConfig } from 'axios';
import {
    ClientUnassignedId,
    ConsentAcceptance,
    DefaultClientLocation,
    Gender,
    IAddress,
    IChapter,
    INamedEntitySummary,
    INoteInsight,
    InputFileSource,
    KeepRecording,
    MedicalNoteType,
    NoteEditorType,
    Organization,
    PagedResponse,
    ParticipantsType,
    Pronoun,
    RecordingContentType,
    SignUpDetails,
    SpeakerEvent,
    TherapeuticApproach,
    TherapySession,
    TherapySessionFormat,
    TherapySessionType,
    User,
    UserRole,
} from '../types';

import {
    ACCEPT_CONSENTS_PATH,
    ACCEPTED_CONSENTS_PATH,
    CREATE_USER_PATH,
    INVITE_LINK_PATH,
    ORGANIZATION_PREF_PATH,
    ORGANIZATIONS_PATH,
    PASSWORD_RESET_LINK_PATH,
    PASSWORD_RESET_PATH,
    PRIVATE_ROOM_LINK_PATH,
    REQUEST_MAGIC_LINK,
    REQUIRED_CONSENTS_PATH,
    SELF_SCHEDULE_SIGN_IN_PATH,
    SIGN_IN_CLIENT_PATH,
    SIGN_UP_PATH,
    SKIP_CLIENT_CONSENTS_PATH,
    THERAPY_SESSION_DETAIL_PATH,
    THERAPY_SESSION_LIST_PATH,
    THERAPY_SESSION_PATH,
    THERAPY_SESSION_V2_PATH,
    UPDATE_PASSWORD_PATH,
    USER_DATA_PATH,
    USER_PATH,
    USERS_LIST_PATH,
    VALIDATE_EMAIL_CODE_PATH,
    VALIDATE_INVITE_PATH,
    VERIFY_AUTH_PATH,
} from './configuration';
import { parsePhone } from '@upheal/utils/phoneNumberUtils';
import { DateTime } from 'luxon';
import {
    GQLDiagnosis,
    GQLEditorSegment,
    GQLModality,
    GQLOrganizationRegion,
    GQLSessionState,
} from '@upheal/graphql/definitions/schema.generated';

export const signInClientRequest = (inviteId: string): AxiosRequestConfig => {
    return {
        method: 'post',
        url: SIGN_IN_CLIENT_PATH,
        data: {
            inviteId: inviteId,
        },
    };
};

export type PagedRequestParams = {
    offset: number;
    pageSize: number;
    sortBy?: { id: string; desc: boolean };
    searchQuery?: string;
};

export const getUsersPagedRequest = (
    params: PagedRequestParams,
): AxiosRequestConfig => {
    const { offset, pageSize, sortBy, searchQuery } = params;

    const sortParams = sortBy
        ? {
              sortBy: sortBy.id,
              sortAscending: !sortBy.desc,
          }
        : {};

    return {
        method: 'get',
        url: USERS_LIST_PATH,
        params: {
            offset,
            pageSize,
            ...sortParams,
            ...(searchQuery && { searchQuery }),
        },
    };
};

export const getPagedTherapySessions = ({
    contractId,
    offset,
    pageSize,
    uninvoiced,
    includeInvoiceStatus,
}: {
    contractId?: string;
    offset: number;
    pageSize: number;
    uninvoiced?: boolean;
    includeInvoiceStatus?: boolean;
}): AxiosRequestConfig => {
    return {
        method: 'get',
        url: THERAPY_SESSION_LIST_PATH, // TODO: v2 variant
        params: {
            ...(contractId && { contractId }),
            offset,
            pageSize,
            ...(uninvoiced !== undefined && { uninvoiced }),
            ...(includeInvoiceStatus !== undefined && { includeInvoiceStatus }),
        },
    };
};

// get therapy session
export const getTherapySessionRequest = (
    sessionId: string,
): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${THERAPY_SESSION_PATH}/${sessionId}`,
    };
};

export type TherapySessionProgressResponse = {
    state: GQLSessionState;
    detail: {
        progressNoteReady: boolean;
    };
    lastProcessingDateUtc: string;
};

export const getTherapySessionProgress = (
    sessionId: string,
): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${THERAPY_SESSION_PATH}/${sessionId}/progress`,
    };
};

export const getTherapySessionDetail = (
    sessionId: string,
): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}`,
    };
};

export const getTherapySessionAnalytics = (
    sessionId: string,
): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}/analytics`,
    };
};

export const updateTherapySessionNoteTitle = (props: {
    sessionId: string;
    noteType: MedicalNoteType;
    title: string;
}): AxiosRequestConfig => {
    const { sessionId, noteType, title } = props;
    return {
        method: 'put',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}/editors/${noteType}`,
        data: {
            title,
        },
    };
};

export const updateTherapySessionNotes = (
    sessionId: string,
    data: {
        [key in NoteEditorType]?: GQLEditorSegment[];
    },
): AxiosRequestConfig => {
    return {
        method: 'put',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}/editors`,
        data: {
            editors: data,
        },
    };
};

// currently used only for marking as "inaccurate"
export const updateNamedEntity = (
    sessionId: TherapySession['sessionId'],
    namedEntity: INamedEntitySummary,
): AxiosRequestConfig => {
    return {
        method: 'put',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}/insights`,
        data: {
            entities: [namedEntity],
        },
    };
};

// currently used only for marking as "inaccurate"
export const updateNoteInsight = (
    sessionId: TherapySession['sessionId'],
    noteInsight: INoteInsight,
    type: MedicalNoteType,
): AxiosRequestConfig => {
    return {
        method: 'put',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}/insights`,
        data: {
            notes: {
                [type]: [noteInsight.text],
            },
        },
    };
};

export const updateChapterRequest = (
    sessionId: TherapySession['sessionId'],
    chapter: IChapter,
): AxiosRequestConfig => {
    return {
        method: 'put',
        url: `${THERAPY_SESSION_DETAIL_PATH}/${sessionId}/insights`,
        data: {
            chapters: [chapter],
        },
    };
};

export enum UserRemoveFields {
    Email = 'EMAIL',
    PhoneNumber = 'PHONE_NUMBER',
    Address = 'ADDRESS',
}

// TBD the reason for having different request structure than for CreateUser is the different format of phone number fields - once unified, we can change
type UpdateUserRequest = {
    firstName?: string | null;
    lastName?: string | null;
    gender?: Gender | null;
    birthDate?: string | null;
    pronoun?: Pronoun | null;
    email?: string | null;
    locale?: string | null;
    role?: UserRole | null;
    phoneNumber?: {
        countryCode: string;
        number: string;
    };
    workPhoneNumber?: {
        countryCode: string;
        number: string;
    };
    organizationId?: string | null;
    referrer?: string | null;
    defaultSessionType?: TherapySessionType | null;
    address?: IAddress | null;
    nationalProviderIdentifier?: string | null;
    therapyLicense?: string | null;
    removeFields?: UserRemoveFields[];
};

type UserUpdateData = {
    firstName?: string | null;
    lastName?: string | null;
    gender?: Gender | null;
    birthDate?: string | null;
    pronoun?: Pronoun | null;
    locale?: string | null;
    email?: string | null;
    phone?: string | null;
    workPhone?: string | null;
    organizationId?: string | null;
    referrer?: string | null;
    defaultSessionType?: TherapySessionType | null;
    address?: IAddress | null;
    nationalProviderIdentifier?: string | null;
    therapyLicense?: string | null;
    removeFields?: UserRemoveFields[];
};

export const updateUser = (
    user: User,
    userData: UserUpdateData,
): AxiosRequestConfig => {
    const {
        firstName,
        lastName,
        gender,
        birthDate,
        pronoun,
        email,
        phone,
        workPhone,
        organizationId,
        referrer,
        defaultSessionType,
        address,
        nationalProviderIdentifier,
        therapyLicense,
        removeFields,
    } = userData;

    const data: UpdateUserRequest = {
        firstName,
        lastName,
        gender,
        birthDate,
        pronoun,
        email: email === user.email ? undefined : email, // workaround BE error "409 Conflict" when updating email to the same value
        organizationId,
        referrer,
        defaultSessionType,
        address,
        nationalProviderIdentifier,
        therapyLicense,
        removeFields,
    };

    const parsedPhone = parsePhone(phone);
    if (parsedPhone) {
        data.phoneNumber = {
            number: parsedPhone.number,
            countryCode: parsedPhone.countryCode,
        };
    }

    data.workPhoneNumber = parsePhone(workPhone);
    // TODO: add option to modify country and language

    return {
        method: 'post',
        url: `${USER_PATH}/${user.id}`,
        data: data,
    };
};

type CreateUserRequest = {
    firstName?: string | null;
    lastName?: string | null;
    gender?: Gender | null;
    birthDate?: string | null;
    pronoun?: Pronoun | null;
    email?: string | null;
    locale?: string | null;
    role?: UserRole | null;
    phoneNumber?: string | null;
    phoneCountryCode?: string | null;
    workPhoneNumber?: {
        countryCode: string;
        number: string;
    };
    organizationId?: string | null;
    referrer?: string | null;
    address?: IAddress;
};

type UserCreateData = {
    firstName: string;
    lastName: string;
    gender?: Gender | null;
    birthDate?: string | null;
    pronoun?: Pronoun | null;
    email?: string | null;
    phone?: string | null;
    workPhone?: string | null;
    role: UserRole;
    organizationId?: string | null;
    referrer?: string | null;
    address?: IAddress;
};

export const createUser = (userData: UserCreateData): AxiosRequestConfig => {
    const {
        firstName,
        lastName,
        gender,
        birthDate,
        pronoun,
        email,
        phone,
        workPhone,
        role,
        organizationId,
        referrer,
        address,
    } = userData;

    const data: CreateUserRequest = {
        firstName,
        lastName,
        gender,
        birthDate,
        pronoun,
        email,
        locale: 'en-US', // TODO make configurable
        role,
        organizationId,
        referrer,
        address,
    };

    const parsedPhone = parsePhone(phone);
    if (parsedPhone) {
        data.phoneNumber = parsedPhone.number;
        data.phoneCountryCode = parsedPhone.countryCode;
    }

    data.workPhoneNumber = parsePhone(workPhone);

    return {
        method: 'post',
        url: CREATE_USER_PATH,
        data: data,
    };
};

export const removeUserRequest = (userId: string): AxiosRequestConfig => {
    return {
        method: 'delete',
        url: `${USER_DATA_PATH}/${userId}`,
    };
};

export const sendConsentEmailRequest = (
    clientId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `/invite/consent/${clientId}/notification`,
        data: {},
    };
};

export const changePassword = (params: {
    oldPassword: string;
    newPassword: string;
    verificationId: string;
    verificationCode: string;
}): AxiosRequestConfig => {
    return {
        method: 'post',
        url: UPDATE_PASSWORD_PATH,
        data: params,
    };
};

export const initiateMultipartUpload = (
    routePrefix: string,
    data?: Record<string, unknown>,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `${routePrefix}/initiate`,
        data,
    };
};

export const getMultipartUploadPartUrl = (
    routePrefix: string,
    data: {
        uploadId: string;
        bucket: string;
        key: string;
        parts: Array<{ partNumber: number }>;
    },
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `${routePrefix}/parts-presigned-url`,
        data,
    };
};

export const createTherapySessionFromFileRequest = (props: {
    contractId?: string;
    recordingUrl?: string;
    streamRecordingId?: string;
    date?: DateTime;
    name?: string;
    keepRecording: KeepRecording;
    sessionType?: TherapySessionType;
    inputFileSource: InputFileSource;
    modalities?: Array<GQLModality['id']>;
    diagnoses?: Array<GQLDiagnosis['id']>;
    nonce?: string;
    clientPronoun?: Pronoun;
    sessionFormat?: TherapySessionFormat;
    recordingContentType?: RecordingContentType;
    noteTemplateIds?: Array<string>;
    participantIds?: Array<string>;
    clientPresence?: boolean;
    clientLocation?: string;
    otp?: string;
}): AxiosRequestConfig => {
    const contractId =
        props.contractId === ClientUnassignedId ||
        props.contractId === undefined
            ? undefined
            : props.contractId;

    const {
        clientPronoun,
        recordingUrl,
        streamRecordingId,
        date = DateTime.now(),
        name,
        keepRecording,
        inputFileSource,
        nonce = recordingUrl,
        sessionFormat,
        recordingContentType = RecordingContentType.Recording,
        noteTemplateIds = [],
        modalities = [],
        // Not passed on FE right now, always default to BE
        diagnoses = undefined,
        participantIds,
        clientPresence,
        clientLocation,
        otp,
    } = props;

    if (!recordingUrl && !streamRecordingId) {
        throw new Error(
            'Either recordingUrl or streamRecordingId must be provided',
        );
    }

    return {
        method: 'post',
        url: THERAPY_SESSION_V2_PATH,
        data: {
            contractId,
            date: date.toUTC().toISO(),
            manualUpload: true,
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            recordingUrl,
            recordingId: streamRecordingId,
            sessionName: name,
            keepRecording: {
                audio: keepRecording === KeepRecording.AudioOnly,
                video: keepRecording === KeepRecording.Full,
                none: keepRecording === KeepRecording.None,
            },
            sessionType: TherapySessionType.Template,
            clientPronoun,
            sessionFormat,
            recordingContentType,
            inputFileSource,
            noteTemplateIds,
            modalities,
            diagnoses,
            participantIds,
            clientPresence,
            clientLocation,
            otp,
        },
        headers: { 'X-Nonce': nonce },
    };
};

export const createTherapySessionFromTextRequest = (props: {
    contractId?: string;
    name?: string;
    nonce?: string;
    sessionFormat?: TherapySessionFormat;
    clientPronoun?: Pronoun;
    cptCodes?: string[];
    // @deprecated
    modality?: TherapeuticApproach;
    modalities?: Array<GQLModality['id']>;
    diagnoses?: Array<GQLDiagnosis['id']>;
    noteTemplateId: string;
    sessionSummary: string;
    participantIds?: Array<string>;
    clientPresence?: boolean;
    date?: Date;
    duration?: number;
    clientLocation?: string;
}): AxiosRequestConfig => {
    const contractId =
        props.contractId === ClientUnassignedId ||
        props.contractId === undefined
            ? undefined
            : props.contractId;

    const {
        name,
        sessionFormat,
        cptCodes,
        modality,
        noteTemplateId,
        sessionSummary,
        clientPronoun,
        participantIds,
        clientPresence,
        modalities = [],
        // Not passed on FE right now, always default to BE
        diagnoses = undefined,
        date = new Date(), // Default to current date if not provided
        duration,
        clientLocation,
    } = props;

    return {
        method: 'post',
        url: THERAPY_SESSION_V2_PATH,
        data: {
            contractId,
            date: DateTime.fromJSDate(date).toUTC().toISO(),
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            sessionName: name,
            manualUpload: false,
            sessionFormat,
            cptCodes,
            modality,
            modalities,
            diagnoses,
            clientPronoun,
            inputFileSource: InputFileSource.TextToNote,
            participantsType: ParticipantsType.DictatedNotes,
            recordingContentType: RecordingContentType.TextToNote,
            noteTemplateIds: [noteTemplateId],
            textToNoteData: {
                text: sessionSummary,
            },
            sessionType: TherapySessionType.Template,
            participantIds,
            clientPresence,
            duration,
            clientLocation,
        },
    };
};

export const createOnlineTherapySessionRequest = ({
    contractId,
    waitingRoomId,
    otp,
    noteTemplateIds = [],
}: {
    contractId: string;
    waitingRoomId?: string;
    sessionType?: TherapySessionType;
    otp?: string;
    noteTemplateIds?: Array<string>;
}): AxiosRequestConfig => {
    const initDate = new Date();
    return {
        method: 'post',
        url: THERAPY_SESSION_PATH,
        data: {
            contractId,
            date: initDate.toISOString(),
            // "duration": "PT45M",
            manualUpload: false,
            inputFileSource: InputFileSource.UphealSession,
            onlineCall: true,
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            waitingRoomId,
            sessionType: TherapySessionType.Template,
            otp,
            noteTemplateIds,
            clientLocation: DefaultClientLocation.Home, // We assume "Home" location for online sessions
        },
    };
};

export const updateTherapySessionRequest = ({
    sessionId,
    newDate,
    newSessionTitle,
    newSessionSummary,
    seen,
    keepRecording,
    newProgressNoteTitle,
    sessionType,
    duration,
    modalities,
    diagnoses,
    sessionFormat,
    cptCodes,
    feMetadata,
    modality,
    textToNoteSessionSummary,
    clientLocation,
}: {
    sessionId: string;
    newDate?: Date;
    newSessionTitle?: string;
    newSessionSummary?: string;
    seen?: boolean;
    keepRecording?: KeepRecording;
    newProgressNoteTitle?: string;
    sessionType?: TherapySessionType;
    duration?: number;
    modalities?: Array<GQLModality['id']>;
    diagnoses?: Array<GQLDiagnosis['id']>;
    sessionFormat?: TherapySessionFormat;
    cptCodes?: string[];
    feMetadata?: TherapySession['feMetadata'];
    modality?: any;
    textToNoteSessionSummary?: string;
    clientLocation?: string;
}): AxiosRequestConfig => {
    type SessionUpdate = {
        sessionId: string;
        date?: string;
        timezoneId?: string;
        sessionName?: string;
        sessionSummary?: string;
        seen?: boolean;
        modality?: TherapeuticApproach;
        modalities?: Array<GQLModality['id']>;
        diagnoses?: Array<GQLDiagnosis['id']>;
        textToNoteSessionSummary?: string;
        keepRecording?: {
            audio: boolean;
            video: boolean;
            none: boolean;
        };
        progressNotesTitle?: string;
        sessionType?: TherapySessionType;
        duration?: number;
        sessionFormat?: TherapySessionFormat;
        cptCodes?: string[];
        feMetadata?: TherapySession['feMetadata'];
        clientLocation?: string;
    };
    const newSessionData: SessionUpdate = {
        sessionId,
        duration,
        sessionFormat,
        cptCodes,
    };
    if (newDate) {
        newSessionData.date = newDate.toISOString();
        newSessionData.timezoneId =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    if (newSessionTitle) {
        newSessionData.sessionName = newSessionTitle;
    }
    if (typeof seen === 'boolean') {
        newSessionData.seen = seen;
    }
    if (newSessionSummary !== null) {
        newSessionData.sessionSummary = newSessionSummary;
    }
    if (keepRecording) {
        newSessionData.keepRecording = {
            audio: keepRecording === KeepRecording.AudioOnly,
            video: keepRecording === KeepRecording.Full,
            none: keepRecording === KeepRecording.None,
        };
    }
    if (newProgressNoteTitle) {
        newSessionData.progressNotesTitle = newProgressNoteTitle;
    }

    if (sessionType) {
        newSessionData.sessionType = sessionType;
    }

    if (feMetadata) {
        newSessionData.feMetadata = feMetadata;
    }

    if (modality) {
        newSessionData.modality = modality;
    }

    if (modalities) {
        newSessionData.modalities = modalities;
    }

    if (textToNoteSessionSummary) {
        newSessionData.textToNoteSessionSummary = textToNoteSessionSummary;
    }

    if (clientLocation) {
        newSessionData.clientLocation = clientLocation;
    }

    if (diagnoses) {
        newSessionData.diagnoses = diagnoses;
    }

    return {
        method: 'put',
        url: THERAPY_SESSION_PATH,
        data: newSessionData,
    };
};

export const reassignTherapySessionRequest = (
    sessionId: string,
    contractId: string,
) => {
    return {
        method: 'put',
        url: `${THERAPY_SESSION_V2_PATH}/${sessionId}/assignment`,
        data: {
            contractId,
        },
    };
};

export const deleteTherapySessionTranscriptRequest = (
    sessionId: string,
): AxiosRequestConfig => {
    return {
        method: 'delete',
        url: `${THERAPY_SESSION_PATH}/${sessionId}/transcript`,
    };
};

export const sendSpeakerEventsRequest = (
    sessionId: string,
    events: SpeakerEvent[],
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `${THERAPY_SESSION_PATH}/${sessionId}/speaker-events`,
        data: {
            events,
        },
    };
};

export const joinWaitingRoom = ({
    therapistId,
    clientId,
    recordingType = 'Cloud',
}: {
    therapistId: string;
    clientId: string;
    recordingType?: 'Local' | 'Cloud';
}): AxiosRequestConfig => {
    return {
        method: 'post',
        url: '/waiting-room',
        data: {
            ownerUserId: therapistId,
            participantUserId: clientId,
            recordingType,
        },
    };
};

export const knockWaitingRoom = (waitingRoomId: string): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `/waiting-room/${waitingRoomId}/knock`,
    };
};

export const unknockWaitingRoom = (
    waitingRoomId: string,
): AxiosRequestConfig => {
    return {
        method: 'delete',
        url: `/waiting-room/${waitingRoomId}/knock`,
    };
};

export const getWaitingRoom = (waitingRoomId: string): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `/waiting-room/${waitingRoomId}`,
    };
};

export const acceptConsentsRequest = (
    consents: Array<ConsentAcceptance>,
    therapistId: string | null,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: ACCEPT_CONSENTS_PATH,
        data: {
            consents,
            consentInitiatorUserId: therapistId,
        },
    };
};

export const skipClientConsentRequest = (
    clientUserId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: SKIP_CLIENT_CONSENTS_PATH,
        data: {
            clientUserId,
        },
    };
};

export const inviteLinkRequest = (userId: string): AxiosRequestConfig => {
    return {
        method: 'post',
        url: INVITE_LINK_PATH,
        data: {
            userId,
        },
    };
};

export const organizationMemberInviteLinkRequest = (
    userId: string,
    organizationId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `${ORGANIZATIONS_PATH}/${organizationId}/users/${userId}/invite`,
        data: {},
    };
};

export const validateInviteRequest = (inviteId: string): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${VALIDATE_INVITE_PATH}/${inviteId}`,
    };
};

export const validateEmailCodeRequest = (code: string): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${VALIDATE_EMAIL_CODE_PATH}/${code}`,
    };
};

export const inviteLinkConsentsRequest = (
    inviteId: string,
): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `/invite/link/${inviteId}/consents`,
    };
};

export const signUpRequest = (details: SignUpDetails): AxiosRequestConfig => {
    return {
        method: 'post',
        url: SIGN_UP_PATH,
        data: details,
    };
};

export const verifyRequest = (
    userId: string,
    phone: string,
): AxiosRequestConfig => {
    const parsedPhone = parsePhone(phone);
    if (!parsedPhone) {
        throw new Error('Invalid phone number');
    }
    return {
        method: 'post',
        url: VERIFY_AUTH_PATH,
        data: {
            userId,
            phoneCountryCode: parsedPhone.countryCode,
            phoneNumber: parsedPhone.number,
        },
    };
};

export const concludeTherapySessionRequest = (
    sessionId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `/therapy-session/${sessionId}/conclude`,
        data: {},
    };
};

export const sendResetPasswordEmailRequest = (
    email: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: PASSWORD_RESET_LINK_PATH,
        data: {
            email,
        },
    };
};

export const validatePasswordResetIdRequest = (
    inviteId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: PASSWORD_RESET_PATH,
        data: {
            inviteId,
        },
    };
};

export const resetPasswordRequest = (
    inviteId: string,
    verificationCode: string,
    password: string,
): AxiosRequestConfig => {
    return {
        method: 'put',
        url: PASSWORD_RESET_PATH,
        data: {
            inviteId,
            verificationCode,
            password,
        },
    };
};

export const privateRoomSignInRequest = (props: {
    email: string;
    roomName: string;
}): AxiosRequestConfig => {
    return {
        method: 'post',
        url: PRIVATE_ROOM_LINK_PATH,
        data: props,
    };
};

export const getOrganizationRequest = (
    organizationId: string,
): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `${ORGANIZATIONS_PATH}/${organizationId}`,
    };
};

export type OrganizationsPagedResponse = PagedResponse<Organization>;

export const organizationsPagedRequest = (
    params: PagedRequestParams,
): AxiosRequestConfig => {
    const { offset, pageSize, sortBy, searchQuery } = params;

    const sortParams = sortBy
        ? {
              sortBy: sortBy.id,
              sortAscending: !sortBy.desc,
          }
        : {};

    return {
        method: 'get',
        url: `${ORGANIZATIONS_PATH}/search`,
        params: {
            offset,
            pageSize,
            ...sortParams,
            ...(searchQuery && { searchQuery }),
        },
    };
};

export const createOrganizationRequest = (name: string): AxiosRequestConfig => {
    return {
        method: 'post',
        url: ORGANIZATIONS_PATH,
        data: {
            name,
        },
    };
};

export enum OrganizationRemoveField {
    Address = 'ADDRESS',
}

export const updateOrganizationRequest = (params: {
    organizationId: string;
    name?: string;
    phoneNumber?: {
        number: string;
        countryCode: string;
    };
    email?: string;
    referredBy?: string;
    extras?: {
        key: string;
        value: string;
    }[];
    allowDataCollectionForAppImprovement?: boolean;
    address?: IAddress;
    removeFields?: OrganizationRemoveField[];
    region?: GQLOrganizationRegion;
    outputLanguage?: string;
    isUphealOS?: boolean;
}): AxiosRequestConfig => {
    const {
        organizationId,
        name,
        email,
        phoneNumber,
        referredBy,
        extras,
        allowDataCollectionForAppImprovement,
        address,
        removeFields,
        region,
        outputLanguage,
        isUphealOS,
    } = params;

    const code = referredBy && referredBy.length > 0 ? referredBy : undefined;

    return {
        method: 'put',
        url: `${ORGANIZATIONS_PATH}/${organizationId}`,
        data: {
            name,
            phoneNumber,
            email,
            referredBy: code,
            extras,
            allowDataCollectionForAppImprovement,
            address,
            removeFields,
            region,
            outputLanguage,
            isUphealOS,
        },
    };
};

export type UsersPagedResponse = PagedResponse<User>;

export const organizationUsersPagedRequest = (
    params: {
        organizationId: string;
        roles: Array<UserRole>;
        ownerUserId?: string;
    } & PagedRequestParams,
): AxiosRequestConfig => {
    const {
        organizationId,
        offset,
        pageSize,
        roles,
        ownerUserId,
        sortBy,
        searchQuery,
    } = params;

    const sortParams = sortBy
        ? {
              sortBy: sortBy.id,
              sortAscending: !sortBy.desc,
          }
        : {};

    return {
        method: 'get',
        url: `${ORGANIZATIONS_PATH}/${organizationId}/users-paged`,
        params: {
            offset,
            pageSize,
            roles: roles.join(','),
            ownerUserId,
            ...sortParams,
            ...(searchQuery && { searchQuery }),
        },
    };
};

export const changeProviderRoleRequest = (params: {
    organizationId: string;
    userId: string;
    role: UserRole;
}): AxiosRequestConfig => {
    const { organizationId, userId, role } = params;
    return {
        method: 'put',
        url: `${ORGANIZATIONS_PATH}/${organizationId}/users/${userId}/role`,
        data: {
            role,
        },
    };
};

export const promoteUserRequest = (params: {
    userId: string;
    organizationId: string;
    role: UserRole;
}): AxiosRequestConfig => {
    const { organizationId, userId, role } = params;
    return {
        method: 'post',
        url: `${ORGANIZATIONS_PATH}/${organizationId}/users/${userId}/promotion`,
        data: {
            role,
        },
    };
};

export const requiredConsentsRequest = (params: {
    organizationId: string;
}): AxiosRequestConfig => {
    const { organizationId } = params;
    return {
        method: 'get',
        url: REQUIRED_CONSENTS_PATH,
        params: {
            organizationId,
        },
    };
};

export const clientConsentsRequest = (params: {
    clientId: string;
}): AxiosRequestConfig => {
    const { clientId } = params;
    return {
        method: 'get',
        url: `${ACCEPTED_CONSENTS_PATH}/${clientId}`,
    };
};

export const confirmConsentForDataProcessing = () => {
    return {
        method: 'post',
        url: '/consent/confirm/data-processing',
    };
};

export const confirmConsentForTelehealthRecordingStorage = () => {
    return {
        method: 'post',
        url: '/consent/confirm/telehealth-recording-storage',
    };
};

export const organizationPreferencesRequest = (params: {
    organizationId: string;
    keyName?: string;
}): AxiosRequestConfig => {
    const { organizationId, keyName } = params;
    return {
        method: 'get',
        url: `${ORGANIZATION_PREF_PATH}/${organizationId}${
            keyName ? `/${keyName}` : ''
        }`,
    };
};

export const updateOrganizationPreferencesRequest = (params: {
    organizationId: string;
    key: string;
    value: any;
}): AxiosRequestConfig => {
    const { organizationId, key, value } = params;
    return {
        method: 'put',
        url: `${ORGANIZATION_PREF_PATH}/${organizationId}`,
        data: {
            preferences: [
                {
                    key,
                    value,
                },
            ],
        },
    };
};

export const exportUserDataRequest = (params: {
    userId: string;
    content: 'all' | 'progress-notes';
}): AxiosRequestConfig => {
    const { userId, content } = params;
    return {
        method: 'get',
        url: `${USER_DATA_PATH}/user/${userId}`,
        params: {
            content,
        },
    };
};

export const exportUserDataStatusRequest = (params: {
    exportId: string;
}): AxiosRequestConfig => {
    const { exportId } = params;
    return {
        method: 'get',
        url: `${USER_DATA_PATH}/${exportId}/status`,
    };
};

const THERAPY_SESSION_LINK_PATH = '/invite/therapy-session/link';
export const therapySessionLinkRequest = (
    clientId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: THERAPY_SESSION_LINK_PATH,
        data: {
            userId: clientId,
        },
    };
};

export const clientPortalInviteLinkRequest = (
    clientId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: '/invite/client-portal/link',
        data: {
            userId: clientId,
        },
    };
};

export const sendInviteEmailRequest = (
    inviteId: string,
): AxiosRequestConfig => {
    return {
        method: 'post',
        url: `/invite/link/${inviteId}/notification`,
    };
};

/**
 * Generates download request for the resulting PDF
 *
 * @param organizationId
 * @param clientId Optional parameter will determine if the resulting PDF will have empty inputs or be filled with client details
 */
export const getConsentPdfRequest = (
    organizationId: string,
    clientId?: string,
): AxiosRequestConfig => {
    const searchParams = new URLSearchParams();

    if (clientId) {
        searchParams.append('clientUserId', clientId);
    }

    const queryString = searchParams.toString();

    const finalUrl = `/privacyPolicy/organization/${organizationId}/pdf${
        Boolean(queryString) ? `?${queryString}` : ''
    }`;

    return {
        url: finalUrl,
        method: 'GET',
        responseType: 'blob',
    };
};

export const selfSchedulingSignInRequest = (props: {
    providerUserId: string;
    clientEmail: string;
}): AxiosRequestConfig => {
    return {
        method: 'post',
        url: SELF_SCHEDULE_SIGN_IN_PATH,
        data: props,
    };
};

export const selfSchedulingStatusRequest = (props: {
    providerUserId: string;
    appointmentTypeId: string;
}): AxiosRequestConfig => {
    return {
        method: 'get',
        url: `/schedule/${props.providerUserId}/${props.appointmentTypeId}/status`,
    };
};

export const clientPortalTriggerMagicLinkRequest = (props: {
    email: string;
}): AxiosRequestConfig => {
    return {
        method: 'post',
        url: REQUEST_MAGIC_LINK,
        data: props,
    };
};
